import React, { useState } from 'react';
import { TestTube, Send, CheckCircle, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_BASE_URL, FLIGHT_AWARE_API_BASE } from '../config/environment.js';
import { authFetch } from '../utils/authFetch';
import FlightAwareService from '../services/flightAwareAPI.js';
import airportTimezoneService from '../services/airportTimezoneService';
import { getUserId } from '../utils/getUserId';

// Component to handle timezone conversion for flight times
const FlightTimeDisplay = ({ timeString, airportCode, label }) => {
  const [formattedTime, setFormattedTime] = React.useState('Loading...');

  React.useEffect(() => {
    const convertTime = async () => {
      try {
        const timezone = await airportTimezoneService.getAirportTimezone(airportCode);
        const date = new Date(timeString);
        
        const result = date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZoneName: 'short',
          timeZone: timezone
        });
        
        // Silent time conversion
        
        setFormattedTime(result);
      } catch (error) {
        setFormattedTime('Time conversion error');
      }
    };

    convertTime();
  }, [timeString, airportCode, label]);

  return formattedTime;
};

// ── Helper: format a Date to YYYY-MM-DD ──────────────────────────────────────
function formatDateYMD(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// ── Helper: get today's local date as YYYY-MM-DD ─────────────────────────────
function getTodayLocal() {
  return formatDateYMD(new Date());
}

// ── Helper: sanitize lookup inputs and compute date range ────────────────────
function buildFlightQuery(lookupForm) {
  const airline = (lookupForm.airline || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  const flightNumber = (lookupForm.flightNumber || '').trim().replace(/[^0-9]/g, '');
  const flightIdent = `${airline}${flightNumber}`;

  const searchDate = lookupForm.date ? new Date(lookupForm.date + 'T00:00:00') : new Date();
  const startDate = formatDateYMD(searchDate);

  const endDateObj = new Date(searchDate);
  endDateObj.setDate(endDateObj.getDate() + 2);
  const endDate = formatDateYMD(endDateObj);

  return { airline, flightNumber, flightIdent, startDate, endDate };
}

// ── Helper: match or pick the best flight from API response ──────────────────
function matchFlight(data, lookupForm, flightIdent, startDate) {
  const targetFlight = {
    departureAirport: lookupForm.departureAirport || null,
    arrivalAirport: lookupForm.arrivalAirport || null,
    flightId: flightIdent,
    departureTime: startDate
  };

  const flightAwareService = new FlightAwareService();
  const matchedFlight = flightAwareService.parseFlightData(data, targetFlight);

  if (matchedFlight) return matchedFlight;

  if (data.flights && data.flights.length > 0) {
    const sortedFlights = [...data.flights].sort((a, b) => {
      const timeA = new Date(a.scheduled_out || a.scheduled_off || a.estimated_out || 0).getTime();
      const timeB = new Date(b.scheduled_out || b.scheduled_off || b.estimated_out || 0).getTime();
      return timeA - timeB;
    });
    return sortedFlights[0];
  }

  return null;
}

// ── Helper: store a time string as UTC ISO ───────────────────────────────────
function storeAsUTC(timeString) {
  if (!timeString) return null;
  return new Date(timeString).toISOString();
}

// ── Helper: extract airport code from various data shapes ────────────────────
function extractAirportCode(endpoint) {
  if (!endpoint) return 'N/A';
  return endpoint.airport || endpoint.code_iata || (endpoint.code && endpoint.code.replace(/^K/, '')) || 'N/A';
}

// ── Helper: pick the right time (estimated if delayed, else scheduled) ───────
const TIME_FIELDS = {
  departure: {
    estimatedRaw: 'estimated_out',
    scheduledRaw: 'scheduled_out',
    nested: 'departure',
  },
  arrival: {
    estimatedRaw: 'estimated_in',
    scheduledRaw: 'scheduled_in',
    nested: 'arrival',
  },
};

function pickTime(flight, direction) {
  const fields = TIME_FIELDS[direction];
  const estimatedRaw = flight[fields.estimatedRaw];
  const scheduledRaw = flight[fields.scheduledRaw];
  const nested = flight[fields.nested];
  const hasDelay = estimatedRaw && scheduledRaw && estimatedRaw !== scheduledRaw;

  if (hasDelay) {
    return nested?.estimated || estimatedRaw || scheduledRaw;
  }
  return nested?.scheduled || scheduledRaw || estimatedRaw;
}

// ── Helper: build flightInfo state from raw flight data ──────────────────────
function buildFlightInfo(flight, airline, flightNumber) {
  return {
    airline,
    flightNumber,
    flightIdent: flight.flightNumber || flight.ident,
    departureAirport: extractAirportCode(flight.departure || flight.origin),
    arrivalAirport: extractAirportCode(flight.arrival || flight.destination),
    departureTime: storeAsUTC(pickTime(flight, 'departure')),
    arrivalTime: storeAsUTC(pickTime(flight, 'arrival')),
    departureGate: flight.departure?.gate || flight.gate_origin,
    arrivalGate: flight.arrival?.gate || flight.gate_destination,
    departureTerminal: flight.departure?.terminal || flight.terminal_origin,
    arrivalTerminal: flight.arrival?.terminal || flight.terminal_destination,
    status: flight.status,
    aircraftType: flight.aircraft?.type || flight.aircraft_type,
    rawData: flight
  };
}

// ── Helper: build trip payload for the tracking API ──────────────────────────
function buildTripPayload(flightInfo, userId) {
  const flightId = `${flightInfo.airline}${flightInfo.flightNumber}`;

  const tripData = {
    userId,
    tripName: `TEST: ${flightInfo.airline} ${flightInfo.flightNumber}`,
    startDate: new Date(flightInfo.departureTime).getTime(),
    endDate: new Date(flightInfo.arrivalTime).getTime(),
    flights: [{
      flightId,
      airline: flightInfo.airline,
      flightNumber: flightInfo.flightNumber,
      departureAirport: flightInfo.departureAirport,
      arrivalAirport: flightInfo.arrivalAirport,
      departureTime: flightInfo.departureTime,
      arrivalTime: flightInfo.arrivalTime,
      status: flightInfo.status || 'scheduled',
      gate: flightInfo.departureGate,
      terminal: flightInfo.departureTerminal,
      isTestFlight: true
    }],
    status: 'active',
    createdAt: new Date().toISOString(),
    isTestFlight: true
  };

  const flightStatus = {
    flightId,
    flightNumber: `${flightInfo.airline}${flightInfo.flightNumber}`,
    airline: flightInfo.airline,
    status: flightInfo.status || 'Scheduled',
    departure: {
      airport: flightInfo.departureAirport,
      scheduled: flightInfo.departureTime,
      estimated: flightInfo.departureTime,
      actual: null,
      gate: flightInfo.departureGate || null,
      terminal: flightInfo.departureTerminal || null,
      delay: 0
    },
    arrival: {
      airport: flightInfo.arrivalAirport,
      scheduled: flightInfo.arrivalTime,
      estimated: flightInfo.arrivalTime,
      actual: null,
      gate: flightInfo.arrivalGate || null,
      terminal: flightInfo.arrivalTerminal || null,
      delay: 0
    },
    aircraft: {
      type: flightInfo.aircraftType || null
    },
    progress: 0,
    lastUpdated: new Date().toISOString(),
    dataSource: 'flightaware_lookup'
  };

  return {
    ...tripData,
    flightStatuses: { [flightId]: flightStatus },
    flightStatusesLastChecked: new Date().toISOString()
  };
}

// ── Helper: parse error response body ────────────────────────────────────────
async function parseErrorResponse(response) {
  let errorMessage = `HTTP error! status: ${response.status}`;
  try {
    const errorData = await response.json();
    if (errorData.error) errorMessage = errorData.error;
    if (errorData.details) errorMessage += ` - ${errorData.details}`;
  } catch (e) {
    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
  }
  return errorMessage;
}

// ── Sub-components for the JSX ───────────────────────────────────────────────

const AccessDenied = () => (
  <div style={{
    padding: '32px',
    maxWidth: '600px',
    margin: '0 auto',
    textAlign: 'center'
  }}>
    <h1 style={{
      fontSize: '24px',
      fontWeight: '700',
      color: '#dc2626',
      marginBottom: '16px'
    }}>
      Access Denied
    </h1>
    <p style={{ color: '#6b7280' }}>
      This page is only accessible to authorized test accounts.
    </p>
  </div>
);

const PageHeader = () => (
  <div style={{
    marginBottom: '32px',
    padding: '20px',
    backgroundColor: '#fef9c3',
    border: '2px solid #fde047',
    borderRadius: '12px'
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '8px'
    }}>
      <TestTube size={32} style={{ color: '#ca8a04' }} />
      <h1 style={{
        fontSize: '32px',
        fontWeight: '700',
        color: '#854d0e',
        margin: 0
      }}>
        Flight Testing
      </h1>
    </div>
    <p style={{
      fontSize: '14px',
      color: '#854d0e',
      margin: 0
    }}>
      ⚠️ Admin Only - Look up real flights and test tracking & notification system
    </p>
  </div>
);

const FlightInfoDisplay = ({ flightInfo, loading, onAddToTracking }) => (
  <div style={{
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    border: '2px solid #10b981'
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '20px'
    }}>
      <h2 style={{
        fontSize: '20px',
        fontWeight: '600',
        color: '#1f2937',
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <CheckCircle size={20} style={{ color: '#10b981' }} />
        Flight Found!
      </h2>
      <span style={{
        padding: '4px 12px',
        backgroundColor: '#dbeafe',
        color: '#1e40af',
        borderRadius: '999px',
        fontSize: '14px',
        fontWeight: '600'
      }}>
        {flightInfo.status || 'Scheduled'}
      </span>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
      <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>FLIGHT</div>
        <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
          {flightInfo.airline} {flightInfo.flightNumber}
        </div>
        {flightInfo.aircraftType && (
          <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
            {flightInfo.aircraftType}
          </div>
        )}
      </div>

      <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>ROUTE</div>
        <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
          {flightInfo.departureAirport} → {flightInfo.arrivalAirport}
        </div>
      </div>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
      <div>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
          DEPARTURE
        </div>
        <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
          <FlightTimeDisplay 
            timeString={flightInfo.departureTime}
            airportCode={flightInfo.departureAirport}
            label="departure"
          />
        </div>
        {flightInfo.departureGate && (
          <div style={{ fontSize: '14px', color: '#6b7280' }}>
            Gate {flightInfo.departureGate}
            {flightInfo.departureTerminal && ` • Terminal ${flightInfo.departureTerminal}`}
          </div>
        )}
      </div>

      <div>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
          ARRIVAL
        </div>
        <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
          <FlightTimeDisplay 
            timeString={flightInfo.arrivalTime}
            airportCode={flightInfo.arrivalAirport}
            label="arrival"
          />
        </div>
        {flightInfo.arrivalGate && (
          <div style={{ fontSize: '14px', color: '#6b7280' }}>
            Gate {flightInfo.arrivalGate}
            {flightInfo.arrivalTerminal && ` • Terminal ${flightInfo.arrivalTerminal}`}
          </div>
        )}
      </div>
    </div>

    <button
      onClick={onAddToTracking}
      disabled={loading}
      style={{
        width: '100%',
        padding: '14px 20px',
        backgroundColor: loading ? '#9ca3af' : '#dc2626',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: loading ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
      }}
    >
      <Send size={20} />
      {loading ? 'Adding to Tracking...' : 'Add to Tracking & Start Monitoring'}
    </button>
  </div>
);

const TestResultDisplay = ({ testResult }) => (
  <div style={{
    padding: '20px',
    borderRadius: '12px',
    backgroundColor: testResult.success ? '#f0fdf4' : '#fef2f2',
    border: `2px solid ${testResult.success ? '#86efac' : '#fecaca'}`
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '12px'
    }}>
      {testResult.success ? (
        <CheckCircle size={24} style={{ color: '#22c55e' }} />
      ) : (
        <TestTube size={24} style={{ color: '#ef4444' }} />
      )}
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: testResult.success ? '#166534' : '#dc2626',
        margin: 0
      }}>
        {testResult.success ? 'Test Flight Created!' : 'Error'}
      </h3>
    </div>

    <p style={{
      fontSize: '14px',
      color: testResult.success ? '#166534' : '#dc2626',
      marginBottom: testResult.success ? '12px' : 0
    }}>
      {testResult.message}
    </p>

    {testResult.success && testResult.flight && (
      <div style={{
        padding: '12px',
        backgroundColor: 'white',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#374151'
      }}>
        <p style={{ margin: '0 0 4px 0' }}>
          <strong>Flight:</strong> {testResult.flight.airline} {testResult.flight.flightNumber}
        </p>
        <p style={{ margin: '0 0 4px 0' }}>
          <strong>Route:</strong> {testResult.flight.departureAirport} → {testResult.flight.arrivalAirport}
        </p>
        <p style={{ margin: '0 0 4px 0' }}>
          <strong>Departure:</strong> {new Date(testResult.flight.departureTime).toLocaleString()}
        </p>
        <p style={{ margin: 0 }}>
          <strong>Trip ID:</strong> {testResult.tripId}
        </p>
      </div>
    )}
  </div>
);

const Instructions = () => (
  <div style={{
    marginTop: '24px',
    padding: '16px',
    backgroundColor: '#f0f9ff',
    borderRadius: '8px',
    border: '1px solid #bae6fd'
  }}>
    <h3 style={{
      fontSize: '16px',
      fontWeight: '600',
      color: '#0369a1',
      marginBottom: '8px'
    }}>
      How This Works:
    </h3>
    <ul style={{
      fontSize: '14px',
      color: '#075985',
      margin: 0,
      paddingLeft: '20px'
    }}>
      <li><strong>Step 1:</strong> Enter airline code (e.g., AA, UA, DL), flight number, and date</li>
      <li><strong>Step 2:</strong> Click "Look Up Flight" - TravelFlow fetches real-time data from FlightAware</li>
      <li><strong>Step 3:</strong> Review flight details (route, times, gates, terminals)</li>
      <li><strong>Step 4:</strong> Click "Add to Tracking" to start monitoring</li>
      <li><strong>After Adding:</strong> System monitors for gate changes, delays, cancellations</li>
      <li><strong>Notifications:</strong> You'll receive SMS alerts for any changes</li>
      <li><strong>Reminders:</strong> Trip reminders sent at 24h, 8h, and departure time</li>
      <li><strong>View:</strong> Check "Trips" page to see your tracked flight</li>
    </ul>
  </div>
);

const LookupForm = ({ lookupForm, loading, onLookupChange, onDateChange, onSubmit }) => (
  <div style={{
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
  }}>
    <h2 style={{
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }}>
      <Search size={20} />
      Look Up Flight
    </h2>

    <form onSubmit={onSubmit}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '6px'
          }}>
            Airline Code *
          </label>
          <input
            type="text"
            value={lookupForm.airline}
            onChange={(e) => onLookupChange('airline', e.target.value)}
            placeholder="AA, UA, DL"
            required
            maxLength={3}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '16px',
              textTransform: 'uppercase'
            }}
          />
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '6px'
          }}>
            Flight Number *
          </label>
          <input
            type="text"
            value={lookupForm.flightNumber}
            onChange={(e) => onLookupChange('flightNumber', e.target.value)}
            placeholder="1234"
            required
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '16px'
            }}
          />
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '6px'
          }}>
            Date *
          </label>
          <input
            type="date"
            value={lookupForm.date}
            onChange={(e) => onDateChange(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '16px'
            }}
          />
        </div>
      </div>

      {/* Airport Fields */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '6px'
          }}>
            From Airport (Optional)
          </label>
          <input
            type="text"
            value={lookupForm.departureAirport || ''}
            onChange={(e) => onLookupChange('departureAirport', e.target.value)}
            placeholder="DFW, LAX, JFK"
            maxLength={4}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '16px',
              textTransform: 'uppercase'
            }}
          />
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            Helps find the specific flight route
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '6px'
          }}>
            To Airport (Optional)
          </label>
          <input
            type="text"
            value={lookupForm.arrivalAirport || ''}
            onChange={(e) => onLookupChange('arrivalAirport', e.target.value)}
            placeholder="LAX, DFW, ORD"
            maxLength={4}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '16px',
              textTransform: 'uppercase'
            }}
          />
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            Helps find the specific flight route
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        style={{
          width: '100%',
          padding: '14px 20px',
          backgroundColor: loading ? '#9ca3af' : '#2563eb',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: loading ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        <Search size={20} />
        {loading ? 'Looking Up Flight...' : 'Look Up Flight'}
      </button>
    </form>
  </div>
);

/**
 * Flight Test Page
 * Admin testing page for dmilliorn+tf1@gmail.com only
 * Purpose: Look up real upcoming flights and test tracking and notifications
 */
const FlightTestPage = ({ user, userAttributes, onTripCreated, onNavigate }) => {
  const [loading, setLoading] = useState(false);
  const [lookupForm, setLookupForm] = useState({
    airline: '',
    flightNumber: '',
    date: getTodayLocal(),
    departureAirport: '',
    arrivalAirport: ''
  });

  const [flightInfo, setFlightInfo] = useState(null);
  const [testResult, setTestResult] = useState(null);

  // Verify authorized user
  const userEmail = userAttributes?.email || user?.signInDetails?.loginId || '';
  const isAuthorized = userEmail === 'dmilliorn+tf1@gmail.com';

  const handleLookupChange = (field, value) => {
    setLookupForm(prev => ({
      ...prev,
      [field]: value.toUpperCase()
    }));
  };

  const handleDateChange = (value) => {
    setLookupForm(prev => ({
      ...prev,
      date: value
    }));
  };

  // Look up flight information from FlightAware
  const handleLookupFlight = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFlightInfo(null);
    setTestResult(null);

    try {
      const { airline, flightNumber, flightIdent, startDate, endDate } = buildFlightQuery(lookupForm);

      toast.loading('Looking up flight information...', { id: 'lookup' });

      const response = await authFetch(
        `${FLIGHT_AWARE_API_BASE}/${encodeURIComponent(flightIdent)}?start=${startDate}&end=${endDate}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch flight information: ${response.status}`);
      }

      const data = await response.json();

      if (!data.flights || data.flights.length === 0) {
        throw new Error('No flights found for this flight number and date');
      }

      const flight = matchFlight(data, lookupForm, flightIdent, startDate);

      // Silent flight data processing

      if (!flight) {
        throw new Error('No flights found for this flight number and date');
      }

      setFlightInfo(buildFlightInfo(flight, airline, flightNumber));
      toast.success('Flight found!', { id: 'lookup' });

    } catch (error) {
      toast.error(error.message || 'Failed to look up flight', { id: 'lookup' });
      setTestResult({
        success: false,
        message: error.message || 'Failed to look up flight information'
      });
    } finally {
      setLoading(false);
    }
  };

  // Add the looked-up flight to tracking
  const handleAddToTracking = async () => {
    if (!flightInfo) return;

    setLoading(true);
    setTestResult(null);

    try {
      const userId = getUserId(user, userAttributes);
      if (!userId) {
        throw new Error('User not authenticated — cannot create test trip');
      }
      const enhancedTripData = buildTripPayload(flightInfo, userId);

      // Silent trip creation

      const response = await authFetch(`${API_BASE_URL}/trips`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(enhancedTripData)
      });

      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      const hasImmediateData = enhancedTripData.flightStatuses && Object.keys(enhancedTripData.flightStatuses).length > 0;
      
      setTestResult({
        success: true,
        tripId: result.trip?.tripId || result.tripId,
        message: hasImmediateData 
          ? 'Flight added with immediate status data! Real-time monitoring active...'
          : 'Flight added to tracking! Monitoring for changes...',
        flight: enhancedTripData.flights[0],
        hasImmediateData
      });

      toast.success(hasImmediateData 
        ? 'Flight added with live data!'
        : 'Flight added to tracking!'
      );

      // Invalidate trips cache to show new trip
      if (onTripCreated && result.trip) {
        onTripCreated(result.trip);
      }

      // Optional: Navigate to trips page to show the new trip
      if (onNavigate) {
        setTimeout(() => {
          onNavigate('trips');
        }, 1000); // Small delay to let user see success message
      }

      // Clear form and flight info
      setLookupForm({
        airline: '',
        flightNumber: '',
        date: '',
        departureAirport: '',
        arrivalAirport: ''
      });
      setFlightInfo(null);

    } catch (error) {
      setTestResult({
        success: false,
        message: error.message || 'Failed to add flight to tracking'
      });
      toast.error('Failed to add flight to tracking');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthorized) {
    return <AccessDenied />;
  }

  return (
    <div style={{
      padding: '32px',
      maxWidth: '800px',
      margin: '0 auto'
    }}>
      <PageHeader />

      <LookupForm
        lookupForm={lookupForm}
        loading={loading}
        onLookupChange={handleLookupChange}
        onDateChange={handleDateChange}
        onSubmit={handleLookupFlight}
      />

      {flightInfo && (
        <FlightInfoDisplay
          flightInfo={flightInfo}
          loading={loading}
          onAddToTracking={handleAddToTracking}
        />
      )}

      {testResult && <TestResultDisplay testResult={testResult} />}

      <Instructions />
    </div>
  );
};

export default FlightTestPage;
